// ═══════════════════════════════════════════════════════════════════════════
// PAGE MEETINGS RECORDS — rendu des lignes de la vue liste
// Extrait de page-meetings-records.ts (découpe god-function `renderMeetingsPage`
// 2026-07) : rendu PUR d'une ligne de réunion + helpers (badges utilisateurs,
// picto calendrier, format de date). Aucun état de closure — `programCodeById`
// et `canWrite` (immuables sur un rendu) sont threadés explicitement. Comportement
// identique à l'inline.
// ═══════════════════════════════════════════════════════════════════════════
//
// Bare refs utilisées (résolues via core-globals.d.ts) : esc, t.

import type { MeetingRecord, MeetingUser } from './page-meetings-records.js';

/** Programme référencé par une réunion : le code s'affiche, le libellé part en
 *  infobulle. Partagé avec la vue « contrôles ». */
export interface ProgramRef {
  code: string;
  name: string;
}

/**
 * Cellule « Programme — Projet » : les codes en gras, les libellés au survol.
 * Les codes seuls tiennent dans la colonne mais ne disent pas grand-chose à
 * qui ne les connaît pas encore par cœur ; l'infobulle comble ça sans élargir
 * le tableau. Exportée : la vue « contrôles » rend la même colonne.
 */
export function programProjectCell(
  m: Pick<MeetingRecord, 'program_id' | 'project_code' | 'project_name'>,
  programById: Map<number, ProgramRef>,
): string {
  const program = m.program_id ? programById.get(m.program_id) : undefined;
  const parts: string[] = [];
  const tips: string[] = [];
  if (program?.code) {
    parts.push(program.code);
    tips.push(program.name || program.code);
  }
  if (m.project_code) {
    parts.push(m.project_code);
    tips.push(m.project_name || m.project_code);
  }
  if (!parts.length) return '—';
  const label = parts.map((c) => `<strong>${esc(c)}</strong>`).join(' — ');
  return `<span title="${esc(tips.join(' — '))}">${label}</span>`;
}

/** Code du comité, libellé au survol. Même principe que la colonne ci-dessus. */
export function committeeCell(m: Pick<MeetingRecord, 'committee_code' | 'committee_name'>): string {
  if (!m.committee_code) return '—';
  const tip = m.committee_name || m.committee_code;
  return `<span title="${esc(tip)}">${esc(m.committee_code)}</span>`;
}

const renderUserBadges = (list: MeetingUser[]): string =>
  list
    .map(
      (u) =>
        `<span class="badge badge-outline" style="margin:1px;cursor:default" title="${esc(u.name)}">${esc(u.trigram)}</span>`,
    )
    .join('');

// Picto « réunion liée à un calendrier » (ical_uid présent côté API → from_calendar).
// Icône Lucide monochrome en currentColor : lisible dans la liste comme sur une
// pastille colorée du calendrier (hérite de la couleur du texte de la cellule/chip).
export const calLinkPicto = (): string =>
  `<span class="mr-cal-picto" title="${esc(t('pageMeetingsRecords.fromCalendar'))}" aria-label="${esc(t('pageMeetingsRecords.fromCalendar'))}" role="img" style="display:inline-flex;vertical-align:middle"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 2v4"/><path d="M16 2v4"/><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M3 10h18"/></svg></span>`;

function formatDate(iso: string): string {
  try {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  } catch {
    return iso;
  }
}

/** Rendu des `<tr>` de la vue liste des réunions (ou ligne « vide » si aucune). */
export function renderMeetingRows(
  rows: MeetingRecord[],
  programById: Map<number, ProgramRef>,
  canWrite: boolean,
): string {
  if (!rows.length) {
    return `<tr><td colspan="9" class="text-center text-muted" style="padding:24px">${esc(t('pageMeetingsRecords.empty'))}</td></tr>`;
  }
  return rows
    .map((m) => {
      const dateCell = m.meeting_date
        ? formatDate(m.meeting_date) + (m.meeting_time ? ` ${m.meeting_time.slice(0, 5)}` : '')
        : '—';
      return `
        <tr>
          <td class="cursor-pointer text-mono text-sm font-bold" style="text-decoration:underline" data-mr-edit="${m.id}">${m.id}</td>
          <td class="cursor-pointer" data-mr-edit="${m.id}" style="color:var(--text);font-weight:500">${m.from_calendar ? calLinkPicto() + ' ' : ''}${esc(m.label)}</td>
          <td style="white-space:nowrap">${programProjectCell(m, programById)}</td>
          <td style="white-space:nowrap">${committeeCell(m)}</td>
          <td style="white-space:nowrap">${esc(dateCell)}</td>
          <td>${renderUserBadges(m.animators)}</td>
          <td>${renderUserBadges(m.participants)}</td>
          <td style="text-align:center">${m.notes_count}</td>
          <td style="white-space:nowrap">
            ${canWrite ? `<button class="btn btn-secondary btn-icon mr-btn-edit" data-id="${m.id}" title="${esc(t('common.edit'))}" style="color:#147A57">✎</button>` : ''}
            ${canWrite ? ` <button class="btn btn-danger btn-icon mr-btn-delete" data-id="${m.id}" title="${esc(t('common.delete'))}" style="margin-left:2px">✕</button>` : ''}
          </td>
        </tr>`;
    })
    .join('');
}

// ── Options des filtres de la barre d'outils ─────────────────────────────────
// Extraites de `renderMeetingsPage` (cliquet fonction, août 2026). Rendu pur :
// la valeur courante du filtre arrive en paramètre, rien n'est lu de la closure.

interface FilterOption {
  id: number;
  /** Texte affiché après le code, séparé par un tiret. Vide = code seul. */
  label: string;
  code: string;
}

/** `<option>` d'un select de filtre, avec la valeur courante sélectionnée. */
export function filterOptions(items: readonly FilterOption[], current: string): string {
  return items
    .map(
      (o) =>
        `<option value="${o.id}"${current === String(o.id) ? ' selected' : ''}>${esc(o.code)}${
          o.label ? ' — ' + esc(o.label) : ''
        }</option>`,
    )
    .join('');
}
